import { Link, useLocation, useNavigate } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import Callout from "components/callout/callout"
import DeletedDocCallout from "@/components/callout/deletedDocCallout"
import { useTranslation } from "react-i18next"
import { textToHTML } from "utils/converters"
import useStorages from "hooks/useStorages"
import Section from "components/layout/section"
import SectionHeader from "components/layout/sectionHeader"
import SectionHeaderTitle from "components/layout/sectionHeaderTitle"
import Button from "components/inputs/button"
import SmallDataBox from "components/dataBox/smallDataBox"
import DataBoxHolder from "components/dataBox/dataBoxHolder"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import SectionHeaderActions from "components/layout/sectionHeaderActions"
import SectionDivider from "components/layout/sectionDivider"
import PageTitle from "features/navigation/pageTitle"
import Tag from "@/components/tags/tag"
import useAccess from "@/hooks/useAccess"
import { AccessRole, DeviceSize, FormStatus } from "@/types"
import ProductList from "@/features/storages/products/productList"
import { paramGuardStorage } from "@/utils/paramGuard"
import IconButton from "@/components/buttons/iconButton"
import useWindowDimensions from "@/hooks/useWindowDimensions"
import useLayout from "@/hooks/useLayout"
import { useState } from "react"
import RightSidebarStorage from "@/components/sidebar/right/RightSidebarStorage"
import RoleTag from "@/components/tags/RoleTag"
import { MainSheetHeaderActions } from "@/components/layout/layoutChrome"

const StoragePage = () => {

    const { t } = useTranslation()
    const location = useLocation()
    const navigate = useNavigate()
    const state = location.state as { new: boolean }
    const { displayIfSize } = useWindowDimensions()

    const { targetStorageDocId: storageId } = useTargetStorage()

    const {
        storage,
        storageStatus,
        restoreStorageAsync,
        isRestoring
    } = useStorages({storageId})

    const {
        getUserRole,
        limitToRoles
    } = useAccess({ storageId: storageId || '' })

    const { addToast } = useLayout()
    const [isRestoringState, setIsRestoringState] = useState(false)

    // Check params and declare safe ones to use in JSX
    if (!paramGuardStorage({storageId, storage, storageStatus, navigate})) {
        return <></>
    }
    const safeStorageId = storageId!;

    const handleRestoreClick = async () => {
        setIsRestoringState(true)
        try {
            await restoreStorageAsync(safeStorageId)
            addToast({
                text: t('storageRestored'),
                colorScheme: 'green'
            })
        } catch (error) {
            addToast({
                text: t('failedToRestore'),
                colorScheme: 'red'
            })
        } finally {
            setIsRestoringState(false)
        }
    }

    // Render storage.
    return (
        <>
            <PageTitle title={storage?.name ? `${t('titleStorage')}: ${storage.name}` : t('titleStorage')} />
            <MainSheetHeaderActions>
                {getUserRole && <RoleTag role={getUserRole} />}
            </MainSheetHeaderActions>
            {storage?.isDeleted && (
                <DeletedDocCallout
                    documentType="storage"
                    deletionType="hard"
                    action={handleRestoreClick}
                    isRestoring={isRestoring || isRestoringState}
                    deletedAt={storage?.keyHistory?.deletedAt}
                />
            )}
            {storageStatus === FormStatus.SUCCESS ?
                storage?.description || storage?.location && <Section>
                    {storage?.description && <p dangerouslySetInnerHTML={{__html: textToHTML(storage.description)}}></p>}
                    {storage?.location && <p><b>{t('location')}: </b>{storage.location}</p>}
                </Section> :
                <Section>
                    <p className="skeleton"></p>
                    <p className="skeleton short"></p>
                </Section>
            }
            <Section>
                <DataBoxHolder>
                    <SmallDataBox
                        label={t(`labelHighStock`)}
                        value={storage?.productsStockStatus?.highStock || 0}
                        colorScheme="green"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                        />
                    <SmallDataBox
                        label={t(`labelLowStock`)}
                        value={storage?.productsStockStatus?.lowStock || 0}
                        colorScheme="yellow"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                        />
                    <SmallDataBox
                        label={t(`labelNoStock`)}
                        value={storage?.productsStockStatus?.noStock || 0}
                        colorScheme="red"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                        />
                    <SmallDataBox
                        label={t(`labelNegativeStock`)}
                        value={storage?.productsStockStatus?.negativeStock || 0}
                        colorScheme="red"
                        isLoading={storageStatus !== FormStatus.SUCCESS}
                        />
                </DataBoxHolder>
            </Section>
            <SectionDivider />
            <Section padX={false}>
                <SectionHeader>
                    <SectionHeaderTitle>
                        <h2>{t(`titleProducts`)}</h2>
                    </SectionHeaderTitle>
                    <SectionHeaderActions>
                        {limitToRoles([AccessRole.EDITOR, AccessRole.OWNER]) && (
                            displayIfSize([DeviceSize.SM]) ?
                                <IconButton colorScheme="blue" className="visible md:invisible" to={`/storage/products/create`}><AddIcon /></IconButton> :
                                <Button wideOnMobile
                                    to={`/storage/products/create`}
                                    design={`primary`}
                                    className="invisible md:visible">
                                    <AddIcon />
                                    <span>{t('buttonNewProduct')}</span>
                                </Button>
                        )}
                    </SectionHeaderActions>
                </SectionHeader>
                {state?.new && <Callout type={`success`}>
                    <p><b>{t(`calloutStorageCreatedTitle`)}</b></p>
                    <p>{t(`calloutStorageCreatedText`)}</p>
                    <Link className="block w-full md:inline-block md:w-fit" to={`/storage/categories/create`}><Button wideOnMobile design="white"><AddIcon />{t(`buttonAddNewCategory`)}</Button></Link>
                    <Link className="block w-full md:inline-block md:w-fit" to={`/storage/products/create`}><Button wideOnMobile design="white"><AddIcon />{t(`buttonAddNewProduct`)}</Button></Link>
                </Callout>}
                <ProductList storageId={safeStorageId} />
            </Section>
            <RightSidebarStorage storageId={safeStorageId} />
        </>
    )
}

export default StoragePage

